import { SCAN26_DEV_PRESETS, Scan26AutofillScope } from './scan26Autofill'

export type Scan26AutofillMode = 'preset' | 'random'

export type Scan26AutofillPrefs = {
  mode: Scan26AutofillMode
  presetId: string
  scope: Scan26AutofillScope
}

export const SCAN26_AUTOFILL_PREFS_KEY = 'dev:scan26-autofill-prefs'
export const SCAN26_AUTOFILL_PREFS_EVENT = 'scan26-autofill-prefs'

const DEFAULT_PREFS: Scan26AutofillPrefs = {
  mode: 'preset',
  presetId: SCAN26_DEV_PRESETS[0]?.id ?? 'baseline_employed',
  scope: 'from_zero',
}

export function loadScan26AutofillPrefs(): Scan26AutofillPrefs {
  try {
    const raw = localStorage.getItem(SCAN26_AUTOFILL_PREFS_KEY)
    if (!raw) {
      return DEFAULT_PREFS
    }
    const parsed = JSON.parse(raw) as Partial<Scan26AutofillPrefs>
    const presetId =
      parsed.presetId && SCAN26_DEV_PRESETS.some((p) => p.id === parsed.presetId)
        ? parsed.presetId
        : DEFAULT_PREFS.presetId
    const mode = parsed.mode === 'random' ? 'random' : 'preset'
    const scope = parsed.scope === 'remaining_only' ? 'remaining_only' : 'from_zero'
    return { mode, presetId, scope }
  } catch {
    return DEFAULT_PREFS
  }
}

export function saveScan26AutofillPrefs(prefs: Scan26AutofillPrefs): void {
  localStorage.setItem(SCAN26_AUTOFILL_PREFS_KEY, JSON.stringify(prefs))
  window.dispatchEvent(new Event(SCAN26_AUTOFILL_PREFS_EVENT))
}

/** Libellé court pour le bouton raccourci : « Remplissage · Nominal · 3/25 » */
export function shortPresetLabel(label: string): string {
  const head = label.split(' — ')[0]?.trim() ?? label
  if (head.length <= 16) {
    return head
  }
  return `${head.slice(0, 14)}…`
}

export function formatScan26AutofillShortcutLabel(
  prefs: Scan26AutofillPrefs,
  answered: number,
  total: number,
): string {
  const progress = `${answered}/${total}`
  if (prefs.mode === 'random') {
    return `Remplissage · Aléatoire · ${progress}`
  }
  const preset = SCAN26_DEV_PRESETS.find((p) => p.id === prefs.presetId)
  const name = preset ? shortPresetLabel(preset.label) : 'Profil'
  return `Remplissage · ${name} · ${progress}`
}

export type Scan26AutofillTooltip = {
  headline: string
  lines: string[]
}

export function formatScan26AutofillTooltip(
  prefs: Scan26AutofillPrefs,
  answered: number,
  total: number,
): Scan26AutofillTooltip {
  const headline = formatScan26AutofillShortcutLabel(prefs, answered, total)
  const scopeLabel =
    prefs.scope === 'from_zero' ? 'Tout refaire (zéro)' : 'Questions restantes seulement'

  if (prefs.mode === 'random') {
    return {
      headline,
      lines: [
        'Mode : réponses aléatoires valides',
        `Portée : ${scopeLabel}`,
        'Applique la config de la modale DEV (onglet Debug)',
      ],
    }
  }

  const preset = SCAN26_DEV_PRESETS.find((p) => p.id === prefs.presetId)
  return {
    headline,
    lines: [
      `Profil : ${preset?.label ?? prefs.presetId}`,
      ...(preset?.description ? [preset.description] : []),
      `Portée : ${scopeLabel}`,
      'Applique la config de la modale DEV (onglet Debug)',
    ],
  }
}

export function autofillSourceFromPrefs(prefs: Scan26AutofillPrefs): 'random' | string {
  return prefs.mode === 'random' ? 'random' : prefs.presetId
}
